/** One row per linked advertising account (flattened from Amazon managerAccounts API). */
export type LinkedAccountRow = {
  profileId?: string;
  accountName?: string;
  accountType?: string;
  accountId?: string;
  marketplaceId?: string;
  managerAccountName?: string;
  managerAccountId?: string;
  dspAdvertiserId?: string;
  countryCode?: string | null;
  currencyCode?: string | null;
};

export type ManagerAccountGroup = {
  managerAccountId?: string;
  managerAccountName?: string;
  accounts: LinkedAccountRow[];
};

export function extractManagerGroups(data: unknown): ManagerAccountGroup[] | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.managerAccounts) || d.managerAccounts.length === 0) return null;

  const groups: ManagerAccountGroup[] = [];
  for (const m of d.managerAccounts) {
    if (!m || typeof m !== 'object') continue;
    const mgr = m as Record<string, unknown>;
    const managerAccountName =
      typeof mgr.managerAccountName === 'string' ? mgr.managerAccountName : undefined;
    const managerAccountId =
      typeof mgr.managerAccountId === 'string' ? mgr.managerAccountId : undefined;
    const linked = mgr.linkedAccounts;
    const accounts: LinkedAccountRow[] = [];
    if (Array.isArray(linked)) {
      for (const item of linked) {
        if (!item || typeof item !== 'object') continue;
        const acc = item as Record<string, unknown>;
        accounts.push({
          profileId: acc.profileId != null ? String(acc.profileId) : undefined,
          accountName: typeof acc.accountName === 'string' ? acc.accountName : undefined,
          accountType: typeof acc.accountType === 'string' ? acc.accountType : undefined,
          accountId: acc.accountId != null ? String(acc.accountId) : undefined,
          marketplaceId: acc.marketplaceId != null ? String(acc.marketplaceId) : undefined,
          dspAdvertiserId:
            typeof acc.dspAdvertiserId === 'string' ? acc.dspAdvertiserId : undefined,
          countryCode:
            typeof acc.countryCode === 'string'
              ? acc.countryCode
              : typeof acc.country_code === 'string'
                ? acc.country_code
                : undefined,
          currencyCode:
            typeof acc.currencyCode === 'string'
              ? acc.currencyCode
              : typeof acc.currency_code === 'string'
                ? acc.currency_code
                : undefined,
          managerAccountName,
          managerAccountId,
        });
      }
    }
    groups.push({ managerAccountId, managerAccountName, accounts });
  }
  return groups;
}

export function extractAccountRows(data: unknown): LinkedAccountRow[] {
  const grouped = extractManagerGroups(data);
  if (grouped) {
    return grouped.flatMap((g) => g.accounts);
  }

  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;

  if (Array.isArray(d.accounts)) return d.accounts as LinkedAccountRow[];
  if (Array.isArray(d.results)) return d.results as LinkedAccountRow[];
  if (Array.isArray(d)) return d as LinkedAccountRow[];
  return [];
}

/** First-seen row per profileId. Rows without a profileId are kept. */
export function uniqueAccountRows(rows: LinkedAccountRow[]): LinkedAccountRow[] {
  const seen = new Set<string>();
  const out: LinkedAccountRow[] = [];
  for (const row of rows) {
    const profileId = row.profileId;
    if (profileId) {
      if (seen.has(profileId)) continue;
      seen.add(profileId);
    }
    out.push(row);
  }
  return out;
}

export function accountProfileKey(row: LinkedAccountRow): string | null {
  const v = row.profileId ?? row.accountId;
  return v != null && String(v) !== '' ? String(v) : null;
}

export function accountLabel(row: LinkedAccountRow): string {
  const n = row.accountName;
  if (typeof n === 'string' && n) return n;
  const k = accountProfileKey(row);
  return k ? `Account ${k}` : 'Unnamed account';
}

/** Dropdown label: account name plus Amazon account id when present. */
export function advertisingAccountSelectLabel(row: LinkedAccountRow): string {
  const name = accountLabel(row);
  const id = row.accountId?.trim();
  if (id && id !== name) return `${name} (${id})`;
  return name;
}

export function rowStableKey(row: LinkedAccountRow, idx: number): string {
  const parts = [row.managerAccountId, row.accountId, row.profileId, row.marketplaceId].filter(
    Boolean
  );
  return parts.length ? parts.join('|') : `row-${idx}`;
}

const CAMPAIGN_TABS = new Set([
  'dashboard',
  'overview',
  'workspace',
  'budget-rules',
  'automations',
  'reports',
  'compare',
  'recommendations',
  'daily-spend',
  'budget-usage',
  'stream',
  'chat',
  'agent',
  'retail',
]);

/** Preserve campaigns sub-route when switching accounts; campaign detail falls back to dashboard. */
export function pathAfterAccountSwitch(newProfileId: string, pathname: string): string {
  const base = `/accounts/${encodeURIComponent(newProfileId)}`;
  const scoped = pathname.match(/^\/accounts\/[^/]+(\/.*)?$/);
  if (!scoped) {
    return `${base}/campaigns/dashboard`;
  }

  const suffix = scoped[1] ?? '';
  const campaignMatch = suffix.match(/^\/campaigns\/([^/]+)(?:\/(.*))?$/);
  if (campaignMatch) {
    const segment = campaignMatch[1];
    if (CAMPAIGN_TABS.has(segment)) {
      const rest = campaignMatch[2] ? `/${campaignMatch[2]}` : '';
      return `${base}/campaigns/${segment}${rest}`;
    }
    return `${base}/campaigns/dashboard`;
  }

  if (suffix === '/campaigns' || suffix.startsWith('/campaigns/')) {
    return `${base}/campaigns/dashboard`;
  }

  return `${base}/campaigns/dashboard`;
}

export function profileIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/accounts\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Dayparting route — skip heavy account/campaign catalog fetches on initial load. */
export function isCampaignStreamPath(pathname: string): boolean {
  return /\/campaigns\/stream(?:-v2)?\/?$/.test(pathname);
}
